/**
 * BudgetConfigDialog (Story 18.2 — Epic 18 Goal & Budget Tracking)
 *
 * Per-client budget configuration as a CENTERED MODAL DIALOG (directive §4.1 —
 * NEVER a side Sheet; the *Dialog.tsx naming convention). The per-client peer
 * of MarkupSettingsDialog (17.2) — same §4.2 scaffold (@radix-ui/react-dialog +
 * AnimatePresence forceMount portal, bg-black/35 overlay, rounded-3xl
 * border-[0.5px] p-8 content, dialog enter/exit {opacity, scale:0.95, y:10}),
 * width tier max-w-md, all fields staggered (§6 form tier 0.05·(i+1)).
 *
 * Contents (one budget SET per client, one period for the whole set):
 *  - PERIOD: segmented Monthly / Quarterly control → periodType.
 *  - TOTAL BUDGET: a USD currency input ($ prefix), optional → the 'all'
 *    sentinel row on save. Cross-platform total spend, works standalone.
 *  - PER-PLATFORM ROWS: one row per CONNECTED ads platform (useClientIntegrations
 *    filtered to AD_SPEND_PLATFORMS, DEDUPED by type — Trap #3; a client can hold
 *    multiple accounts per platform, render the platform ONCE). Logo + name +
 *    USD input. An empty row = no budget for that platform.
 *  - ZERO ads platforms → explanatory empty state; the Total field stays usable.
 *
 * Saves the full set via PUT /api/clients/:id/budgets (replace semantics — an
 * empty total AND empty rows clears the set; the server deletes-then-inserts).
 *
 * §4.6: always-mounted at the call site (no {open && ...} guard); form state
 * resets/seeds on OPEN, not close. Close is blocked while the save is pending.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "motion/react";
import { Button, Input } from "@provenleap/ui";
import { CurrencyDollar, PlugsConnected } from "@phosphor-icons/react";
import { useClientIntegrations } from "../../hooks/useClientIntegrations";
import {
  useClientBudgets,
  useSaveClientBudgets,
} from "../../hooks/useClientBudgets";
import { AD_SPEND_PLATFORMS, platformBadge } from "../../lib/integrations";
import type { ToastType } from "../../hooks/useToast";

// ============================================================================
// Props
// ============================================================================

interface BudgetConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  clientName?: string;
  showToast: (type: ToastType, message: string) => void;
}

// ============================================================================
// Constants
// ============================================================================

type BudgetPeriod = "monthly" | "quarterly";

// SYNC — apps/web/src/lib/integrations.ts AD_SPEND_PLATFORMS mirrors the
// server BUDGET_SPEND_METRIC_BY_PLATFORM keys (budget-pacing.ts). This set
// decides which per-platform ROWS render; the server's Zod layer is the
// authority that rejects unknown platforms.
const AD_SPEND_SET = new Set<string>(AD_SPEND_PLATFORMS);

const CARD_BORDER = "0.5px solid var(--hairline)";

// ============================================================================
// Validation (mirrors the server AC #2 — positive, finite, ≤ 100,000,000)
// ============================================================================

/** Returns an inline error string for a raw amount field, or null if valid. */
function validateAmount(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null; // empty = "no budget" for this scope
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return "Enter a number";
  if (n <= 0) return "Must be greater than 0";
  if (n > 100_000_000) return "Max $100,000,000";
  return null;
}

// ============================================================================
// Segmented period control (compact §4.3 — keeps the dialog at max-w-md)
// ============================================================================

function PeriodSegmentedControl({
  value,
  onChange,
}: {
  value: BudgetPeriod;
  onChange: (p: BudgetPeriod) => void;
}) {
  const options: { key: BudgetPeriod; label: string }[] = [
    { key: "monthly", label: "Monthly" },
    { key: "quarterly", label: "Quarterly" },
  ];
  return (
    <div
      role="radiogroup"
      aria-label="Budget period"
      className="grid grid-cols-2 gap-1 rounded-2xl bg-muted/40 p-1 shadow-xs"
      style={{ border: CARD_BORDER }}
    >
      {options.map((opt) => {
        const active = value === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            role="radio"
            aria-checked={active}
            data-testid={`budget-period-${opt.key}`}
            onClick={() => onChange(opt.key)}
            className={`cursor-pointer rounded-xl px-3 py-1.5 text-[13px] font-medium transition-all ${
              active
                ? "bg-background text-foreground shadow-xs"
                : "text-muted-foreground hover:text-foreground"
            }`}
            style={active ? { border: CARD_BORDER } : undefined}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ============================================================================
// USD currency input ($ prefix) + inline error
// ============================================================================

function CurrencyField({
  value,
  onChange,
  error,
  idBase,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  error: string | null;
  idBase: string;
  ariaLabel: string;
}) {
  return (
    <div className="space-y-1">
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-muted-foreground">
          $
        </span>
        <Input
          type="text"
          inputMode="decimal"
          data-testid={`${idBase}-amount`}
          aria-label={ariaLabel}
          aria-invalid={error ? true : undefined}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g. 5000"
          className="rounded-2xl pl-7 shadow-xs"
        />
      </div>
      {error ? <p className="text-[12px] text-destructive">{error}</p> : null}
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export function BudgetConfigDialog({
  open,
  onOpenChange,
  clientId,
  clientName,
  showToast,
}: BudgetConfigDialogProps) {
  const { budgets, isLoading: budgetsLoading } = useClientBudgets(
    open ? clientId : undefined,
  );
  const { integrations, isLoading: integrationsLoading } = useClientIntegrations(
    open ? clientId : undefined,
  );
  const saveMutation = useSaveClientBudgets(clientId);
  const pending = saveMutation.isPending;

  const [period, setPeriod] = useState<BudgetPeriod>("monthly");
  const [totalValue, setTotalValue] = useState("");
  // Raw amount strings keyed by platform slug (empty = no budget for it).
  const [platformValues, setPlatformValues] = useState<Record<string, string>>(
    {},
  );

  // Connected ads platforms, DEDUPED by type (Trap #3), excluding paused rows.
  const connectedAdTypes = useMemo(() => {
    const set = new Set<string>();
    for (const i of integrations) {
      if (i.syncStatus === "paused") continue;
      if (AD_SPEND_SET.has(i.type)) set.add(i.type);
    }
    return set;
  }, [integrations]);

  // §4.6 — seed form state ONCE per open session, the FIRST time the data has
  // finished loading (never on close, which flashes mid-exit; never on a
  // mid-edit refetch, which would discard in-progress edits).
  const seededRef = useRef(false);
  const dataLoading = budgetsLoading || integrationsLoading;
  useEffect(() => {
    if (!open) {
      seededRef.current = false;
      return;
    }
    if (seededRef.current || dataLoading) return;
    seededRef.current = true;

    const byScope = new Map<string, number>();
    let seededPeriod: BudgetPeriod = "monthly";
    for (const b of budgets) {
      byScope.set(b.integrationType, b.amount);
      // One period applies to the whole set — adopt the stored one.
      if (b.periodType === "monthly" || b.periodType === "quarterly") {
        seededPeriod = b.periodType;
      }
    }
    setPeriod(seededPeriod);
    setTotalValue(byScope.has("all") ? String(byScope.get("all")) : "");

    const next: Record<string, string> = {};
    for (const type of connectedAdTypes) {
      next[type] = byScope.has(type) ? String(byScope.get(type)) : "";
    }
    setPlatformValues(next);
  }, [open, dataLoading, budgets, connectedAdTypes]);

  // Render rows: connected ads platforms only, alphabetical.
  const platformRows = useMemo(
    () =>
      Array.from(connectedAdTypes)
        .sort((a, b) => a.localeCompare(b))
        .map((type) => {
          const badge = platformBadge(type);
          return {
            integrationType: type,
            displayName: badge?.displayName ?? type,
            logoSrc: badge?.logoSrc ?? null,
          };
        }),
    [connectedAdTypes],
  );

  // ── Validation ──────────────────────────────────────────────────────────
  const totalError = validateAmount(totalValue);
  const platformErrors = useMemo(() => {
    const errs: Record<string, string | null> = {};
    for (const [type, raw] of Object.entries(platformValues)) {
      errs[type] = validateAmount(raw);
    }
    return errs;
  }, [platformValues]);

  const hasErrors =
    totalError !== null ||
    Object.values(platformErrors).some((e) => e !== null);

  // ── Close guard (§4.2) ────────────────────────────────────────────────────
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next && pending) return;
      onOpenChange(next);
    },
    [pending, onOpenChange],
  );

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (hasErrors) return;

    const totalTrimmed = totalValue.trim();
    const totalAmount = totalTrimmed === "" ? null : Number(totalTrimmed);

    const platforms = Object.entries(platformValues)
      .filter(([, raw]) => raw.trim() !== "")
      .map(([integrationType, raw]) => ({
        integrationType,
        amount: Number(raw.trim()),
      }));

    try {
      await saveMutation.mutateAsync({ periodType: period, totalAmount, platforms });
      showToast("success", "Budgets saved");
      onOpenChange(false);
    } catch (err) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to save budgets",
      );
    }
  }, [
    hasErrors,
    totalValue,
    platformValues,
    period,
    saveMutation,
    showToast,
    onOpenChange,
  ]);

  function setPlatform(type: string, raw: string) {
    setPlatformValues((prev) => ({ ...prev, [type]: raw }));
  }

  // Stagger: header(0) → period(1) → total(2) → per-platform header(3) →
  // rows(4..) → footer.
  let staggerIndex = 0;
  const nextDelay = () => 0.05 * (staggerIndex++ + 1);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>

            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-[calc(100vw-2rem)] sm:max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg max-h-[85vh] overflow-y-auto outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                {/* Header */}
                <motion.div
                  className="flex flex-col space-y-1.5 text-left"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: nextDelay() }}
                >
                  <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight flex items-center gap-2">
                    <CurrencyDollar size={18} className="text-foreground" />
                    Budget settings
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    {clientName
                      ? `Set ${clientName}'s ad spend budgets and track them on the dashboard.`
                      : "Set this client's ad spend budgets and track them on the dashboard."}
                  </DialogPrimitive.Description>
                </motion.div>

                {/* Budget period */}
                <motion.div
                  className="mt-6 space-y-3"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: nextDelay() }}
                >
                  <p className="text-[13px] font-medium text-foreground">
                    Budget period
                  </p>
                  <PeriodSegmentedControl value={period} onChange={setPeriod} />
                </motion.div>

                {/* Total budget */}
                <motion.div
                  className="mt-6 space-y-3"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: nextDelay() }}
                >
                  <p className="text-[13px] font-medium text-foreground">
                    Total {period} budget
                  </p>
                  <CurrencyField
                    idBase="budget-total"
                    ariaLabel="Total budget amount"
                    value={totalValue}
                    onChange={setTotalValue}
                    error={totalError}
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Across all connected advertising platforms. Leave empty for
                    no total budget.
                  </p>
                </motion.div>

                {/* Per-platform budgets */}
                <motion.div
                  className="mt-6"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: nextDelay() }}
                >
                  <p className="text-[13px] font-medium text-foreground">
                    Per-platform budgets
                  </p>
                </motion.div>

                <div className="mt-2">
                  {platformRows.length === 0 ? (
                    <motion.div
                      className="flex flex-col items-center justify-center rounded-2xl py-6 text-center"
                      style={{ border: CARD_BORDER }}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: nextDelay() }}
                      data-testid="budget-platforms-empty"
                    >
                      <div className="mb-2 rounded-full bg-muted p-2.5">
                        <PlugsConnected
                          size={20}
                          className="text-muted-foreground"
                        />
                      </div>
                      <p className="max-w-[18rem] px-4 text-[13px] text-muted-foreground">
                        Connect an advertising platform to set per-platform
                        budgets.
                      </p>
                    </motion.div>
                  ) : (
                    platformRows.map((row) => {
                      const error = platformErrors[row.integrationType] ?? null;
                      const idBase = `budget-platform-${row.integrationType}`;
                      const rowDelay = platformRows.length > 6 ? 0.03 : 0.05;
                      return (
                        <motion.div
                          key={row.integrationType}
                          data-testid="budget-platform-row"
                          data-platform={row.integrationType}
                          className="border-b-[0.5px] border-input py-3 last:border-b-0"
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: rowDelay * (staggerIndex++ + 1) }}
                        >
                          <div className="flex items-center gap-3">
                            {row.logoSrc && (
                              <img
                                src={row.logoSrc}
                                alt=""
                                title={row.displayName}
                                className="h-4 w-4 shrink-0 rounded-sm"
                              />
                            )}
                            <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-foreground">
                              {row.displayName}
                            </span>
                          </div>
                          <div className="mt-2 pl-7">
                            <CurrencyField
                              idBase={idBase}
                              ariaLabel={`Budget for ${row.displayName}`}
                              value={platformValues[row.integrationType] ?? ""}
                              onChange={(v) =>
                                setPlatform(row.integrationType, v)
                              }
                              error={error}
                            />
                          </div>
                        </motion.div>
                      );
                    })
                  )}
                </div>

                {/* Footer */}
                <motion.div
                  className="mt-6 flex justify-end gap-3"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * (staggerIndex++ + 1) }}
                >
                  <Button
                    variant="outline"
                    type="button"
                    className="rounded-2xl bg-background text-[13px] cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                    style={{ border: CARD_BORDER }}
                    onClick={() => handleOpenChange(false)}
                    disabled={pending}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="brand"
                    type="button"
                    className="rounded-2xl text-[13px] cursor-pointer"
                    onClick={handleSave}
                    disabled={pending || hasErrors}
                    data-testid="budget-save"
                  >
                    {pending ? "Saving…" : "Save"}
                  </Button>
                </motion.div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
